import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { StatusMessage } from '../StatusMessage/StatusMessage';
/**
 * Bronson FormSection component.
 * @see https://bronson.vwfs.io/default/components/detail/bronson-form-section.html
 * @constructor
 */
export function FormSection({ children, className, defaultOpen = false, disabled, onChange, onClick, status, subtitle, title, testId, ...otherProps }) {
    const [open, setOpen] = useState(defaultOpen);
    const [expanded, setExpanded] = useState(false);
    const panelRef = useRef(null);
    useEffect(() => {
        setOpen(defaultOpen);
        if (defaultOpen) {
            setExpanded(true);
        }
    }, [defaultOpen]);
    function toggleOpen() {
        setOpen(!open);
        if (onChange) {
            onChange(!open);
        }
    }
    const divClassNameList = classNames('c-form-section', {
        'is-open': open,
        // Convenience prop from Bronson variants.
        'c-form-section--error': status === 'error',
        'c-form-section--success': status === 'success',
    }, className).trim();
    const sectionHeaderClassNameList = classNames('c-form-section__header', className).trim();
    const sectionPanelClassNameList = classNames('c-form-section__panel', { 'is-collapsed': !open && !expanded }, { 'is-expanded': open && expanded }, className).trim();
    function renderHeaderContent() {
        return (React.createElement(React.Fragment, null,
            React.createElement("span", { className: "c-form-section__title" },
                React.createElement("span", { className: "c-form-section__title-text", role: "heading", "aria-level": 3 }, title)),
            renderIfFormSectionSubtitle()));
    }
    function renderIfFormSectionSubtitle() {
        if (subtitle) {
            return React.createElement("span", { className: "c-form-section__subtitle" }, subtitle);
        }
        return null;
    }
    function renderIfStatus() {
        if (status) {
            return (React.createElement("span", { className: "c-form-section__status-icon", "aria-hidden": "true" },
                React.createElement(StatusMessage, { status: status })));
        }
    }
    return (
    /**
     * Need to pass `[inert=""]` as React currently does not support HTMLElement.inert mapping.
     * @see https://github.com/facebook/react/pull/24730
     * @TODO: Remove once React supports direct `[inert]` usage.
     * @see BRON-11871
     */
    // eslint-disable-next-line jsx-a11y/role-supports-aria-props
    React.createElement("section", { ...otherProps, "data-testid": testId, className: divClassNameList, "aria-disabled": disabled, 
        // @ts-ignore @TODO: Remove once React supports inerts, @see BRON-11871.
        inert: disabled ? '' : null },
        React.createElement("button", { className: sectionHeaderClassNameList, "aria-expanded": open, onClick: () => !disabled && [toggleOpen(), onClick && onClick()], type: "button" },
            renderIfStatus(),
            React.createElement("span", { className: "c-form-section__header-content" }, renderHeaderContent()),
            React.createElement("span", { className: "c-form-section__header-icon" })),
        React.createElement("div", { className: sectionPanelClassNameList, "aria-hidden": !open, 
            // @ts-ignore @TODO: Remove once React supports inerts, @see BRON-11871.
            inert: open ? null : '', ref: panelRef, onTransitionEnd: (event) => {
                if (event.target === panelRef?.current) {
                    if (open) {
                        setExpanded(true);
                    }
                    else {
                        setExpanded(false);
                    }
                }
            } },
            React.createElement("div", { className: "c-form-section__content" }, children))));
}
